import React, { useEffect, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { X, Pencil, Download, Trash2 } from "lucide-react";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";
import { MaterialRequestReceiptPDF } from "./PurchaseOrderReceipt";
import EllipsisMenu from "components/UI_components/EllipsisMenu";

export default function MaterialRequest({ onClose, Request, onApprove, onReject, onProceedToRFQ }) {
  const normalizeRequest = (request) => {
    if (!request) {
      return {
        material_request_lines: [],
        items: 0,
      };
    }

    const lines = Array.isArray(request.material_request_lines)
      ? request.material_request_lines
      : [];

    return {
      ...request,
      code: request.code || request.mr_number,
      items: request.items ?? lines.length,
      raisedBy: request.raisedBy || request.created_by || "-",
      expectedBy: request.expectedBy || request.due_date || null,
      material_request_lines: lines,
    };
  };

  const [organisationId] = useState(
    JSON.parse(localStorage.getItem("organization"))?.id
  );
  const [selectedItems, setSelectedItems] = useState([]);
  const { addAlert } = useAlert();
  const [warehouseMaterials, setWarehouseMaterials] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [showRejectComment, setShowRejectComment] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editLines, setEditLines] = useState([]);
  const [editComment, setEditComment] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [requestData, setRequestData] = useState(() => normalizeRequest(Request));
  const rejectionReason =
    requestData?.comment ||
    requestData?.rejection_reason ||
    requestData?.reject_reason ||
    requestData?.rejected_comment ||
    requestData?.rejection_comment ||
    "";

  const getMaterialMeta = (line) => {
    if (!line) return "";
    const specification = line?.specification || line?.material_spec || "";
    const subcategory = line?.subcategory || "";
    const brand = line?.brand_name || line?.brand || "";
    return [specification, subcategory, brand].filter(Boolean).join(" | ");
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getMaterials = async () => {
    try {
      const res = await fetch(`${BASE_URL}/org/warehouse/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ org_id: organisationId }),
      });

      if (!res.ok) {
        throw new Error("Failed to fetch materials");
      }

      const data = await res.json();
      console.log("Fetched warehouse materials:", data);
      setWarehouseMaterials(data);
    } catch (err) {
      console.error("Error fetching materials:", err);
      addAlert("Failed to load materials from library", "error");
    }
  };

  const getPurchaseOrders = async () => {
    if (!organisationId) return;
    try {
      const res = await fetch(`${BASE_URL}/inventory/purchase-orders/org/${organisationId}/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
      });
      if (res.ok) {
        const data = await res.json();
        setPurchaseOrders(data);
      }
    } catch (err) {
      console.error("Error fetching purchase orders:", err);
    }
  };

  useEffect(() => {
    getMaterials();
    getPurchaseOrders();
  }, [organisationId]);

  const fetchMaterialRequestDetail = async (
    requestId = requestData?.id,
    { shouldUpdateState = true } = {}
  ) => {
    if (!requestId) return null;

    const response = await fetch(
      `${BASE_URL}/inventory/material-requests/${requestId}/`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message ||
        errorData?.error ||
        "Failed to refresh material request"
      );
    }

    const data = await response.json();
    const normalizedRequest = normalizeRequest(data);
    if (shouldUpdateState) {
      setRequestData(normalizedRequest);
    }
    return normalizedRequest;
  };

  useEffect(() => {
    let isMounted = true;

    const syncRequest = async () => {
      const normalizedRequest = normalizeRequest(Request);
      setRequestData(normalizedRequest);

      if (!Request?.id) return;

      try {
        const latestRequest = await fetchMaterialRequestDetail(Request.id, {
          shouldUpdateState: false,
        });
        if (!isMounted || !latestRequest) return;
        setRequestData(latestRequest);
      } catch (err) {
        console.error("Error refreshing material request details:", err);
      }
    };

    syncRequest();

    return () => {
      isMounted = false;
    };
  }, [Request]);

  // Check if MR line is cancelled via the associated PO or PO line
  const checkIsCancelledInPO = (mrLineId) => {
    return purchaseOrders.some((po) => {
      const isPOCancelled = po.status?.toLowerCase() === "cancelled";
      const line = po.lines?.find((l) => l.mr_lines === mrLineId);
      
      return line && (isPOCancelled || line.status?.toLowerCase() === "cancelled");
    });
  };

  // Derive effective status to cascade PO cancellation down to MR lines
  const requestLinesWithStatus = requestData.material_request_lines.map((item) => ({
    ...item,
    effectiveStatus: checkIsCancelledInPO(item.id) ? "CANCELLED" : item.status,
  }));

  const handleSendFromWarehouse = async () => {
    try {
      const requestBody = {
        lines: selectedItems.map((item) => ({
          id: item.id,
          quantity: item.quantity,
        })),
      };
      const response = await fetch(
        `${BASE_URL}/inventory/material-requests/${requestData.id}/send-from-warehouse/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        addAlert(
          errorData.error || "Failed to send material request from warehouse",
          "error"
        );
        throw new Error("Failed to send material request from warehouse");
      }
      const data = await response.json();
      console.log("Material request sent from warehouse:", data);
      addAlert("Material request sent from warehouse successfully", "success");
      onClose();
    } catch (err) {
      console.error("Error sending material request from warehouse:", err);
    }
  };

  const handleProceedToRFQ = async () => {
    try {
      const requestBody = {
        organization: organisationId,
        material_request: requestData.id,
        sites: requestData.sites,
        lines: selectedItems.map((item) => ({
          material: item.material,
          quantity: item.quantity,
          mr_lines: item.id,
        })),
      };
      const response = await fetch(`${BASE_URL}/inventory/rfq/create/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error("Failed to create RFQ from material request");
      }
      const data = await response.json();
      console.log("RFQ created from material request:", data);
      addAlert("RFQ created from material request successfully", "success");
      onClose();
      setTimeout(() => {
        onProceedToRFQ();
      }, 1000);
    } catch (err) {
      console.error("Error creating RFQ from material request:", err);
      addAlert("Failed to create RFQ from material request", "error");
    }
  };

  const handleApproveReject = async (action) => {
    if (action === "rejected" && !showRejectComment) {
      setShowRejectComment(true);
      return;
    }

    try {
      const requestBody = { action };
      if (action === "rejected" && rejectComment.trim()) {
        requestBody.comment = rejectComment.trim();
      }

      const response = await fetch(
        `${BASE_URL}/inventory/material-requests/${requestData.id}/approve-reject/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to ${action} material request`);
      }
      const data = await response.json();
      console.log(`Material request ${action}:`, data);
      addAlert(`Material request ${action} successfully`, "success");
      onClose();
      setTimeout(() => {
        if (action === "approved") {
          onApprove();
        } else {
          onReject();
        }
      }, 1000);
    } catch (err) {
      console.error(`Error trying to ${action} material request:`, err);
      addAlert(`Failed to ${action} material request`, "error");
    }
  };

  const handleUpdate = async () => {
    try {
      const requestBody = {
        comment: editComment,
        sites: requestData.sites,
        lines: editLines.map((line) => ({
          id: line.id,
          material: line.material,
          quantity: parseFloat(line.quantity),
        })),
      };

      const response = await fetch(
        `${BASE_URL}/inventory/material-requests/${requestData.id}/update/`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update material request");
      }

      const data = await response.json();
      console.log("Material request updated:", data);
      const refreshedRequest = await fetchMaterialRequestDetail(requestData.id);
      addAlert("Material request updated successfully", "success");
      setSelectedItems([]);
      setEditComment(refreshedRequest?.comment || "");
      setIsEditing(false);
      // onClose(); 
      // if (onApprove) onApprove(); 
    } catch (err) {
      console.error("Error updating material request:", err);
      addAlert(err.message || "Failed to update material request", "error");
    }
  };

  const startEditing = () => {
    setEditLines(
      requestData.material_request_lines.map((line) => ({
        id: line.id,
        material: line.material,
        quantity: line.quantity,
      }))
    );
    setEditComment(requestData.comment || "");
    setIsEditing(true);
  };

  const handleDownloadRequest = async () => {
    try {
      setIsDownloading(true);

      const blob = await pdf(
        <MaterialRequestReceiptPDF
          request={requestData}
          warehouseMaterials={warehouseMaterials}
        />,
      ).toBlob();

      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = `Material_Request_${
        requestData?.mr_number || requestData?.code || "receipt"
      }.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Error downloading material request receipt:", err);
      addAlert("Failed to download material request receipt", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleEditRequest = () => {
    setShowRejectComment(false);
    setRejectComment("");
    startEditing();
  };

  const handleDelete = async () => {
    if (!requestData?.id) return;

    setIsDeleting(true);
    setDeleteError("");

    try {
      const response = await fetch(
        `${BASE_URL}/inventory/material-requests/${requestData.id}/delete/`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok || data?.status === false || data?.success === false) {
        throw new Error(
          data?.message || data?.error || "Failed to delete material request"
        );
      }

      addAlert("Material request deleted successfully", "success");
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      const message = err.message || "Failed to delete material request";
      setDeleteError(message);
      addAlert(message, "error");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative w-full md:w-1/2 h-full bg-white border-l border-gray-200 shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
          <div className="flex items-center">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Material Request
              </h2>
              <div className="mt-0.5 flex items-center gap-2">
                <p className="text-xs text-gray-900">
                  {requestData?.mr_number || requestData?.code || "N/A"}
                </p>
                <span className="text-xs font-medium text-purple-900 bg-purple-100 px-2 py-0.5 rounded-full">
                  {requestData.site_name}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <EllipsisMenu
              items={[
                ...(requestData.status === "PENDING_APPROVAL"
                  ? [
                    {
                      key: "edit",
                      label: "Edit",
                      icon: Pencil,
                      onClick: handleEditRequest,
                    },
                    { type: "separator" },
                  ]
                  : []),
                {
                  key: "download",
                  label: isDownloading ? "Generating..." : "Download",
                  icon: Download,
                  onClick: handleDownloadRequest,
                  disabled: isDownloading,
                },
                {
                  key: "delete",
                  label: "Delete",
                  icon: Trash2,
                  danger: true,
                  onClick: () => {
                    setDeleteError("");
                    setShowDeleteConfirm(true);
                  },
                },
              ]}
              buttonAriaLabel="Material request actions"
            />
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Site Name + Download button on same row */}
          <div className="mb-4" />
          {/* Request Info */}
          <div className="rounded-xl p-4 mb-6 border-[1px] border-[#C3C3C3]">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-base font-semibold text-[#232323]">
                  {requestData.mr_number}
                </div>
              </div>
              <div className="text-left">
                <div className="text-sm text-[#737373]">Raised by</div>
                <div className="text-sm font-medium text-[#232323]">
                  {requestData.raisedBy}
                </div>
              </div>
              <div className="text-left">
                <div className="text-sm text-[#737373] mb-1">
                  Delivery expected by
                </div>
                <div className="text-sm font-medium text-[#232323]">
                  {formatDate(requestData.expectedBy)}
                </div>
              </div>
            </div>
          </div>

          {requestData.status === "REJECTED" && (
            <div className="rounded-xl p-4 mb-6 border border-red-200 bg-red-50">
              <div className="text-sm font-semibold text-red-700 mb-1">
                Reason for Rejection
              </div>
              <div className="text-sm text-red-900 whitespace-pre-wrap">
                {rejectionReason || "No rejection reason provided."}
              </div>
            </div>
          )}

          {/* Content Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-semibold text-gray-900">Content</h3>
            <span className="text-sm text-gray-500">
              Total {requestData.items} items requested
            </span>
          </div>

          {/* select all checkbox */}
          {requestData.status !== "PENDING_APPROVAL" && (
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-gray-300"
                color="#3B0069"
                style={{ accentColor: "#3B0069" }}
                checked={
                  selectedItems.length ===
                  requestLinesWithStatus.filter(
                    (item) => item.effectiveStatus === "DRAFT"
                  ).length && selectedItems.length > 0
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedItems(
                      requestLinesWithStatus.filter(
                        (item) => item.effectiveStatus === "DRAFT"
                      )
                    );
                  } else {
                    setSelectedItems([]);
                  }
                }}
                disabled={
                  requestData.status === "COMPLETED" ||
                  requestData.status === "REJECTED" ||
                  requestLinesWithStatus.filter((item) => item.effectiveStatus === "DRAFT").length === 0
                }
              />
              <span className="text-sm text-[#737373] font-light ml-2">
                Select All
              </span>
            </div>
          )}

          {/* Material Items */}
          <div className="space-y-3">
            {requestLinesWithStatus.map((item, index) => {
              const materialMeta = getMaterialMeta(item);
              const isClickable =
                item.effectiveStatus === "DRAFT" &&
                requestData.status !== "COMPLETED" &&
                requestData.status !== "REJECTED" &&
                requestData.status !== "PENDING_APPROVAL";
                
              const statusConfig = {
                COMPLETED: {
                  color: "bg-green-100 text-green-700",
                  label: "Completed",
                },
                SENT: { color: "bg-yellow-100 text-yellow-700", label: "Sent" },
                CANCELLED: {
                  color: "bg-red-100 text-red-700",
                  label: "Cancelled",
                },
                DRAFT: { color: "", label: "" },
              };

              return (
                <div
                  key={item.id}
                  className={`px-2 py-2 rounded-lg ${!isClickable && !isEditing
                    ? "opacity-60" : ""
                    }`}
                  style={{
                    boxShadow:
                      " 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
                  }}
                >
                  {/* Item Header */}
                  <div className="relative grid grid-cols-[1fr_170px_170px] items-center gap-3 py-1 px-6 border-b-[0.5px] border-[#C3C3C3]">
                    <div className="flex items-center gap-3">
                    {requestData.status !== "PENDING_APPROVAL" && (
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300"
                        color="#3B0069"
                        style={{ accentColor: "#3B0069" }}
                        disabled={!isClickable}
                        checked={
                          selectedItems.find((i) => i.id === item.id) !==
                          undefined
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems((prev) => [...prev, item]);
                          } else {
                            setSelectedItems((prev) =>
                              prev.filter((i) => i.id !== item.id)
                            );
                          }
                        }}
                      />
                    )}
                    <span className="text-sm text-[#737373] font-light">
                      Material {index + 1}
                    </span>
                    {item.effectiveStatus !== "DRAFT" && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusConfig[item.effectiveStatus]?.color
                          }`}
                      >
                        {statusConfig[item.effectiveStatus]?.label}
                      </span>
                    )}
                    </div>
                    <span className="text-sm text-[#737373] font-light">
                      Warehouse
                    </span>
                    <span className="text-sm text-[#737373] font-light text-left">
                      Requested
                    </span>
                  </div>

                  {/* Item Details */}
                  <div className="grid grid-cols-[1fr_170px_170px] items-start gap-3 py-1 px-6">
                    <div className="flex flex-col items-start gap-1">
                      <span className="text-sm font-medium text-[#232323]">
                        {item?.material_name}
                      </span>
                      {materialMeta && (
                        <span className="text-sm font-normal text-[#737373]">
                          {materialMeta}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col items-start gap-1 min-w-0">
                      <span className="text-sm font-medium text-[#232323]">
                        {warehouseMaterials.find(
                          (m) => m.material.id === item.material
                        )?.current_quantity || 0}{" "}
                        {item?.uom}
                      </span>
                      <span className="text-sm font-normal text-[#737373]">
                        In stock
                      </span>
                    </div>
                    <div className="flex flex-col items-start gap-1 min-w-0">
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            value={
                              editLines.find((l) => l.id === item.id)
                                ?.quantity || ""
                            }
                            onChange={(e) => {
                              const newVal = e.target.value;
                              setEditLines((prev) =>
                                prev.map((l) =>
                                  l.id === item.id
                                    ? { ...l, quantity: newVal }
                                    : l
                                )
                              );
                            }}
                            className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                          />
                          <span className="text-sm font-medium text-[#232323]">
                            {item?.uom}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm font-medium text-[#232323]">
                          {item?.quantity} {item?.uom}
                        </span>
                      )}
                      <span className="text-sm font-normal text-[#737373]">
                        Requested quantity
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer Actions */}
        <div
          className="border-t border-gray-200 px-6 py-4 flex gap-3"
          style={{
            display:
              requestData.status === "COMPLETED" ||
                requestData.status === "PENDING_APPROVAL" ||
                requestData.status === "REJECTED"
                ? "none"
                : "flex",
          }}
        >
          <button
            onClick={handleSendFromWarehouse}
            style={{
              cursor:
                requestData.status === "COMPLETED" || selectedItems.length === 0
                  ? "not-allowed"
                  : "pointer",
              ...(requestData.status === "COMPLETED" || selectedItems.length === 0
                ? { backgroundColor: "#E0E0E0", color: "#A3A3A3" }
                : {}),
            }}
            className="flex-1 bg-purple-100 text-purple-900 font-medium py-3 px-4 rounded-lg transition-colors hover:bg-[#3B0069] hover:text-white"
            disabled={
              requestData.status === "COMPLETED" || selectedItems.length === 0
            }
          >
            Send from warehouse
          </button>
          <button
            onClick={handleProceedToRFQ}
            style={{
              cursor:
                requestData.status === "COMPLETED" || selectedItems.length === 0
                  ? "not-allowed"
                  : "pointer",
              ...(requestData.status === "COMPLETED" || selectedItems.length === 0
                ? { backgroundColor: "#E0E0E0", color: "#A3A3A3" }
                : {}),
            }}
            className="flex-1 bg-purple-100 text-purple-900 font-medium py-3 px-4 rounded-lg transition-colors hover:bg-[#3B0069] hover:text-white"
            disabled={
              requestData.status === "COMPLETED" || selectedItems.length === 0
            }
          >
            Proceed to RFQ
          </button>
        </div>

        {/* Approval/Rejection Section */}
        <div
          className="border-t border-gray-200 px-6 py-4"
          style={{
            display:
              requestData.status === "PENDING_APPROVAL" ||
                isEditing ||
                showRejectComment
                ? "block"
                : "none",
          }}
        >
          {/* Rejection/Edit Comment Field */}
          {(showRejectComment || isEditing) && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isEditing ? "Update Comment" : "Reason for rejection (optional)"}
              </label>
              <textarea
                value={isEditing ? editComment : rejectComment}
                onChange={(e) =>
                  isEditing
                    ? setEditComment(e.target.value)
                    : setRejectComment(e.target.value)
                }
                placeholder={isEditing ? "Add a comment for this update..." : "Enter reason for rejection..."}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                rows={3}
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdate}
                  className="flex-1 bg-purple-900 hover:bg-purple-800 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  Save Changes
                </button>
              </>
            ) : requestData.status === "PENDING_APPROVAL" ? (
              <>
                <button
                  onClick={() => {
                    if (showRejectComment) {
                      setShowRejectComment(false);
                      setRejectComment("");
                    } else {
                      handleApproveReject("rejected");
                    }
                  }}
                  className="flex-1 bg-gray-100 hover:bg-red-600 text-gray-700 hover:text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  {showRejectComment ? "Cancel" : "Reject"}
                </button>
                {showRejectComment && (
                  <button
                    onClick={() => handleApproveReject("rejected")}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                  >
                    Confirm Rejection
                  </button>
                )}
                {!showRejectComment && (
                  <>
                    <button
                      onClick={startEditing}
                      className="flex-1 bg-gray-100 hover:bg-purple-600 text-gray-700 hover:text-white font-medium py-3 px-4 rounded-lg transition-colors"
                    >
                      Edit Request
                    </button>
                    <button
                      onClick={() => handleApproveReject("approved")}
                      className="flex-1 bg-gray-100 hover:bg-green-600 text-gray-700 hover:text-white font-medium py-3 px-4 rounded-lg transition-colors"
                    >
                      Approve
                    </button>
                  </>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <p className="text-base font-semibold text-gray-900">
                Delete Material Request
              </p>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteError("");
                }}
                className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                disabled={isDeleting}
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="space-y-3 px-6 py-4 text-sm text-gray-700">
              <p>
                Are you sure you want to delete this material request? This
                action cannot be undone.
              </p>
              {deleteError && (
                <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 border border-red-100">
                  {deleteError}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteError("");
                }}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors disabled:opacity-70"
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
